import React, { useCallback, useEffect, useState } from "react";
import { mixingAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { trimmedDecimal } from "../utils/numberFormat";
import { useMembership } from "../context/MembershipContext";
import EmptyState from "../components/EmptyState";

/**
 * Raw stock for mixing: bases, flavours, nicotine, additives and packaging.
 *
 * Costs are stored to four decimal places, because 0.038 TND/ml rounded to
 * 0.04 would be a 5% error on every batch the shop ever mixes. Read back,
 * though, three is plenty and a whole number should just look like one —
 * `trimmedDecimal` is display-only, the stored value keeps its precision.
 */
const TYPES = ["base", "flavor", "nicotine", "additive", "packaging"];

const BLANK = {
  type: "flavor",
  name: "",
  brand: "",
  unit: "ml",
  stock_quantity: "",
  // What the owner actually knows: the receipt total. unit_cost — what the
  // rest of mixing prices recipes from — is split across stock_quantity at
  // submit time, so nobody has to do that division themselves.
  total_cost: "",
  low_stock_threshold: "",
  nicotine_mg_per_ml: "",
  pg_ratio: "",
  vg_ratio: "",
  recommended_percentage: "",
  size_ml: "",
};

const DOSED_TYPES = ["flavor", "additive"];

const Materials = () => {
  const { t } = useI18n();
  const { can } = useMembership();
  const currency = useShopCurrency();
  const unitLabel = (unit) =>
    unit === "ml" || unit === "pcs" ? t(`mixing.materials.units.${unit}`) : unit;
  const typeLabel = (type) =>
    TYPES.includes(type) ? t(`mixing.materials.types.${type}`) : type;

  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [topUp, setTopUp] = useState(null);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [sizeEdit, setSizeEdit] = useState(null);
  const [sizeEditValue, setSizeEditValue] = useState("");

  // Rounded to the same 4 decimals the column stores, so the preview shown
  // while typing is exactly what gets saved rather than a longer-looking
  // division result.
  const quantity = parseFloat(form.stock_quantity);
  const totalCost = parseFloat(form.total_cost);
  const unitCostPreview =
    quantity > 0 && totalCost >= 0
      ? Math.round((totalCost / quantity) * 10000) / 10000
      : null;

  const load = useCallback(async () => {
    try {
      const { data } = await mixingAPI.materials();
      setMaterials(data.materials);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("mixing.materials.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const report = (err, fallback) =>
    setError(
      err.response?.data?.message || err.response?.data?.error || fallback,
    );

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    // eslint-disable-next-line no-unused-vars
    const { total_cost, ...rest } = form;

    // Empty means "not applicable" rather than zero — a flavour has no
    // nicotine strength, and sending 0 would claim it does
    const payload = Object.fromEntries(
      Object.entries({ ...rest, unit_cost: unitCostPreview ?? "" }).filter(
        ([, value]) => value !== "",
      ),
    );

    try {
      await mixingAPI.createMaterial(payload);
      setForm(BLANK);
      setAdding(false);
      await load();
    } catch (err) {
      report(err, t("mixing.materials.addFailed"));
    } finally {
      setBusy(false);
    }
  };

  const receive = async (material) => {
    setBusy(true);

    try {
      await mixingAPI.adjustMaterial(
        material.id,
        parseFloat(topUpAmount),
        t("mixing.materials.stockReceived"),
      );
      setTopUp(null);
      setTopUpAmount("");
      await load();
    } catch (err) {
      report(err, t("mixing.materials.updateFailed"));
    } finally {
      setBusy(false);
    }
  };

  const retire = async (material) => {
    try {
      await mixingAPI.deleteMaterial(material.id);
      await load();
    } catch (err) {
      report(err, t("mixing.materials.retireFailed"));
    }
  };

  const saveSize = async (material) => {
    setBusy(true);

    try {
      await mixingAPI.updateMaterial(material.id, {
        // Blank clears it back to "every size"
        size_ml: sizeEditValue === "" ? null : Number(sizeEditValue),
      });
      setSizeEdit(null);
      setSizeEditValue("");
      await load();
    } catch (err) {
      report(err, t("mixing.materials.updateFailed"));
    } finally {
      setBusy(false);
    }
  };

  const grouped = materials.reduce((acc, material) => {
    (acc[material.type] = acc[material.type] || []).push(material);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("mixing.materials.title")}
          </h1>
          <p className="text-gray-600">{t("mixing.materials.subtitle")}</p>
        </div>
        {can("mixing.edit") && (
          <button
            onClick={() => setAdding((open) => !open)}
            className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
          >
            {adding ? t("common.cancel") : t("mixing.materials.add")}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {adding && (
        <form
          onSubmit={submit}
          className="bg-white rounded-xl shadow-lg p-6 space-y-4"
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.materials.type")}
              </span>
              <select
                value={form.type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    type: e.target.value,
                    unit: e.target.value === "packaging" ? "pcs" : "ml",
                  })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                {TYPES.map((value) => (
                  <option key={value} value={value}>
                    {typeLabel(value)}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.materials.name")}
              </span>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.materials.howMuch", { unit: unitLabel(form.unit) })}
              </span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                required
                value={form.stock_quantity}
                onChange={(e) =>
                  setForm({ ...form, stock_quantity: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.materials.totalCost", { currency })}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.total_cost}
                onChange={(e) =>
                  setForm({ ...form, total_cost: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <span className="text-xs text-gray-500">
                {unitCostPreview !== null
                  ? t("mixing.materials.costPerComputed", {
                      amount: `${trimmedDecimal(unitCostPreview, 3)} ${currency}`,
                      unit: unitLabel(form.unit),
                    })
                  : t("mixing.materials.totalCostHint")}
              </span>
            </label>
          </div>

          {form.type === "nicotine" && (
            <label className="block max-w-xs">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.materials.strength")}
              </span>
              <input
                type="number"
                step="0.1"
                min="0"
                required
                value={form.nicotine_mg_per_ml}
                onChange={(e) =>
                  setForm({ ...form, nicotine_mg_per_ml: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <span className="text-xs text-gray-500">
                {t("mixing.materials.strengthHint")}
              </span>
            </label>
          )}

          {DOSED_TYPES.includes(form.type) && (
            <label className="block max-w-xs">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.materials.recommendedDose")}
              </span>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                dir="ltr"
                value={form.recommended_percentage}
                onChange={(e) =>
                  setForm({ ...form, recommended_percentage: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <span className="text-xs text-gray-500">
                {t("mixing.materials.recommendedDoseHint")}
              </span>
            </label>
          )}

          {form.type === "packaging" && (
            <label className="block max-w-xs">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.materials.sizeMl")}
              </span>
              <input
                type="number"
                step="1"
                min="1"
                dir="ltr"
                value={form.size_ml}
                onChange={(e) => setForm({ ...form, size_ml: e.target.value })}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <span className="text-xs text-gray-500">
                {t("mixing.materials.sizeMlHint")}
              </span>
            </label>
          )}

          {form.type === "base" && (
            <div className="grid gap-4 sm:grid-cols-2 max-w-md">
              <label className="block">
                <span className="text-sm font-medium text-gray-700">
                  {t("mixing.materials.pg")}
                </span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.pg_ratio}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      pg_ratio: e.target.value,
                      vg_ratio:
                        e.target.value === ""
                          ? ""
                          : String(100 - Number(e.target.value)),
                    })
                  }
                  className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">
                  {t("mixing.materials.vg")}
                </span>
                <input
                  type="number"
                  readOnly
                  value={form.vg_ratio}
                  className="mt-1 w-full px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-600"
                />
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
          >
            {busy ? t("common.saving") : t("mixing.materials.submit")}
          </button>
        </form>
      )}

      {Object.entries(grouped).map(([type, group]) => (
        <div key={type} className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            {typeLabel(type)}
          </h2>
          <ul className="divide-y divide-gray-100">
            {group.map((material) => {
              const low =
                Number(material.stock_quantity) <=
                Number(material.low_stock_threshold);

              return (
                <li key={material.id} className="py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">
                        {material.name}
                        {material.nicotine_mg_per_ml && (
                          <span className="ms-2 text-xs text-gray-500" dir="ltr">
                            {material.nicotine_mg_per_ml} mg/ml
                          </span>
                        )}
                        {material.pg_ratio !== null &&
                          material.type === "base" && (
                            <span className="ms-2 text-xs text-gray-500" dir="ltr">
                              {material.pg_ratio}PG/{material.vg_ratio}VG
                            </span>
                          )}
                        {material.recommended_percentage &&
                          DOSED_TYPES.includes(material.type) && (
                            <span className="ms-2 text-xs text-gray-500" dir="ltr">
                              {trimmedDecimal(material.recommended_percentage, 1)}%
                            </span>
                          )}
                        {material.type === "packaging" &&
                          (material.size_ml ? (
                            <span className="ms-2 text-xs text-gray-500" dir="ltr">
                              {material.size_ml} ml
                            </span>
                          ) : (
                            <span className="ms-2 text-xs text-gray-400">
                              {t("mixing.materials.everySize")}
                            </span>
                          ))}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t("mixing.materials.perUnit", {
                          amount: `${trimmedDecimal(material.unit_cost, 3)} ${currency}`,
                          unit: unitLabel(material.unit),
                        })}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <span
                        className={`text-sm font-semibold ${
                          low ? "text-amber-700" : "text-gray-700"
                        }`}
                      >
                        {Number(material.stock_quantity).toFixed(1)}{" "}
                        {unitLabel(material.unit)}
                        {low && t("mixing.materials.low")}
                      </span>
                      {can("mixing.edit") && (
                        <button
                          onClick={() => {
                            setTopUp(topUp === material.id ? null : material.id);
                            setTopUpAmount("");
                          }}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                        >
                          {t("mixing.materials.received")}
                        </button>
                      )}
                      {material.type === "packaging" && can("mixing.edit") && (
                        <button
                          onClick={() => {
                            setSizeEdit(
                              sizeEdit === material.id ? null : material.id,
                            );
                            setSizeEditValue(material.size_ml || "");
                          }}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                        >
                          {t("mixing.materials.setSize")}
                        </button>
                      )}
                      {can("mixing.delete") && (
                        <button
                          onClick={() => retire(material)}
                          className="text-sm text-gray-400 hover:text-red-600"
                        >
                          {t("mixing.materials.retire")}
                        </button>
                      )}
                    </div>
                  </div>

                  {topUp === material.id && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input
                        type="number"
                        step="0.01"
                        autoFocus
                        placeholder={t("mixing.materials.howMany", {
                          unit: unitLabel(material.unit),
                        })}
                        value={topUpAmount}
                        onChange={(e) => setTopUpAmount(e.target.value)}
                        className="w-48 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      />
                      <button
                        onClick={() => receive(material)}
                        disabled={!topUpAmount || busy}
                        className="px-4 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                      >
                        {t("mixing.materials.addToStock")}
                      </button>
                    </div>
                  )}

                  {sizeEdit === material.id && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        autoFocus
                        dir="ltr"
                        placeholder={t("mixing.materials.everySize")}
                        value={sizeEditValue}
                        onChange={(e) => setSizeEditValue(e.target.value)}
                        className="w-48 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      />
                      <button
                        onClick={() => saveSize(material)}
                        disabled={busy}
                        className="px-4 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                      >
                        {t("mixing.materials.saveSize")}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {materials.length === 0 && (
        <EmptyState screen="materials" message={t("mixing.materials.empty")} />
      )}
    </div>
  );
};

export default Materials;
